import type { GridStackWidget } from 'gridstack';
import { BOOLEAN_WIDGET_TYPE, MOTOR_MULTI_WIDGET_TYPE } from '../Components/Graphs/sensor-utils';
import { COUNTDOWN_TIMER_WIDGET_TYPE } from './countdownTimerCodeUtils';
import { parseDashboardWidgetContent } from './sensor-stream-tracker';

export const DEFAULT_WIDGET_MIN_W = 2;
export const DEFAULT_WIDGET_MIN_H = 2;
export const DEFAULT_WIDGET_MAX_W = 6;
export const DEFAULT_WIDGET_MAX_H = 6;
/** Wider max width for line-chart widgets (e.g. multi-axis motor charts). */
export const CHART_WIDGET_MAX_W = 10;
/** Minimum size for multi-axis motor/mechanism charts. */
export const MOTOR_MULTI_WIDGET_MIN_W = 4;
export const MOTOR_MULTI_WIDGET_MIN_H = 4;

/**
 * Widget types that render as line charts via `GraphWidget` and benefit from a wider max width.
 * Mirrors the `showGraphWidget` branch in `WidgetBase`.
 */
export function isLineChartWidgetType(type: string, dataKey?: string): boolean {
  if (!type) return false;
  if (type === 'pos' && dataKey === 'all') return false;
  if (type === 'speed') return false;
  if (type === BOOLEAN_WIDGET_TYPE) return false;
  if (type === COUNTDOWN_TIMER_WIDGET_TYPE) return false;
  if (type === 'orientation' || type === 'field' || type === 'console' || type === 'timer') return false;
  return true;
}

export function isMotorMultiWidgetType(type: string): boolean {
  return type === MOTOR_MULTI_WIDGET_TYPE;
}

export function resolveWidgetMinSize(type: string): { minW: number; minH: number } {
  if (isMotorMultiWidgetType(type)) {
    return { minW: MOTOR_MULTI_WIDGET_MIN_W, minH: MOTOR_MULTI_WIDGET_MIN_H };
  }
  return { minW: DEFAULT_WIDGET_MIN_W, minH: DEFAULT_WIDGET_MIN_H };
}

/** Combines drawer metadata with per-type minimums (e.g. motor_multi 4x4). */
export function resolveEffectiveWidgetMinSize(type: string, drawerMinW?: number, drawerMinH?: number): { minW: number; minH: number } {
  const typeMinSize = resolveWidgetMinSize(type);
  return {
    minW: Math.max(drawerMinW ?? DEFAULT_WIDGET_MIN_W, typeMinSize.minW),
    minH: Math.max(drawerMinH ?? DEFAULT_WIDGET_MIN_H, typeMinSize.minH),
  };
}

export function resolveWidgetMaxSize(type: string, dataKey?: string): { maxW: number; maxH: number } {
  if (isLineChartWidgetType(type, dataKey)) {
    return { maxW: CHART_WIDGET_MAX_W, maxH: DEFAULT_WIDGET_MAX_H };
  }
  return { maxW: DEFAULT_WIDGET_MAX_W, maxH: DEFAULT_WIDGET_MAX_H };
}

export function resolveWidgetMinSizeFromContent(content: string | undefined): { minW: number; minH: number } {
  const parsed = parseDashboardWidgetContent(content);
  if (!parsed) {
    return { minW: DEFAULT_WIDGET_MIN_W, minH: DEFAULT_WIDGET_MIN_H };
  }
  return resolveWidgetMinSize(parsed.name);
}

export function resolveWidgetMaxSizeFromContent(content: string | undefined): { maxW: number; maxH: number } {
  const parsed = parseDashboardWidgetContent(content);
  if (!parsed) {
    return { maxW: DEFAULT_WIDGET_MAX_W, maxH: DEFAULT_WIDGET_MAX_H };
  }
  return resolveWidgetMaxSize(parsed.name, parsed.dataKey);
}

export function resolveWidgetSizeConstraints(
  type: string,
  dataKey?: string,
  drawerMinW?: number,
  drawerMinH?: number
): { minW: number; minH: number; maxW: number; maxH: number } {
  return {
    ...resolveEffectiveWidgetMinSize(type, drawerMinW, drawerMinH),
    ...resolveWidgetMaxSize(type, dataKey),
  };
}

function parseDrawerMinDimension(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** Resolves drag-preview / placement min size from drawer metadata and widget type. */
export function resolveDragWidgetMinSize(type: string | undefined, drawerMinW: unknown, drawerMinH: unknown): { minW: number; minH: number } {
  const parsedMinW = parseDrawerMinDimension(drawerMinW);
  const parsedMinH = parseDrawerMinDimension(drawerMinH);
  if (type) {
    return resolveEffectiveWidgetMinSize(type, parsedMinW, parsedMinH);
  }
  return {
    minW: parsedMinW ?? DEFAULT_WIDGET_MIN_W,
    minH: parsedMinH ?? DEFAULT_WIDGET_MIN_H,
  };
}

/** Applies min/max size constraints when loading or creating dashboard widgets. */
export function normalizeWidgetSizeConstraints(widget: GridStackWidget): GridStackWidget {
  const { minW: typeMinW, minH: typeMinH } = resolveWidgetMinSizeFromContent(widget.content);
  const { maxW: typeMaxW, maxH: typeMaxH } = resolveWidgetMaxSizeFromContent(widget.content);
  const minW = Math.max(widget.minW ?? typeMinW, typeMinW);
  const minH = Math.max(widget.minH ?? typeMinH, typeMinH);

  return {
    ...widget,
    minW,
    minH,
    maxW: Math.max(widget.maxW ?? typeMaxW, typeMaxW),
    maxH: Math.max(widget.maxH ?? typeMaxH, typeMaxH),
    w: Math.max(widget.w || minW, minW),
    h: Math.max(widget.h || minH, minH),
  };
}
